import { getPayorConfig } from "@utils/payorConfig";
import React from "react";

import { Alert, Button, Dialog, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ApiReportTemplatePayorTypeChoices } from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { CatalightSubmissionDialog } from "@components/ReportCompletionAndExport/CatalightSubmissionDialog";

interface PayorSubmissionDialogProps {
  open: boolean;
  payorType: ApiReportTemplatePayorTypeChoices | null | undefined;
  reportId: string;
  clientName: string;
  dateOfBirth?: string;
  reportTypeLabel?: string;
  onSubmitToPayor: () => void;
  onViewReport: () => void;
  onClose: () => void;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  submissionError?: boolean;
  submissionErrorMessage?: string;
  isResubmission?: boolean;
}

export const PayorSubmissionDialog: React.FC<PayorSubmissionDialogProps> = ({
  open,
  payorType,
  reportId,
  clientName,
  dateOfBirth,
  reportTypeLabel = "Initial Assessment",
  onSubmitToPayor,
  onViewReport,
  onClose,
  isSubmitting = false,
  isSubmitDisabled = false,
  submissionError = false,
  submissionErrorMessage = "",
  isResubmission = false,
}) => {
  const copy = getPayorConfig(payorType);
  if (!copy) return null;

  // PROA-1261: Catalight uses the metadata-collection interstitial instead of the
  // plain confirmation modal, for both initial submission and resubmission. On
  // resubmission the recommendations re-derive from the (possibly edited) report
  // and the editable offer opens cleared, so the BCBA re-enters hours against the
  // current recommended values.
  if (payorType === ApiReportTemplatePayorTypeChoices.Catalight) {
    return (
      <CatalightSubmissionDialog
        open={open}
        reportId={reportId}
        clientName={clientName}
        dateOfBirth={dateOfBirth}
        reportTypeLabel={reportTypeLabel}
        onSubmitToPayor={onSubmitToPayor}
        onViewReport={onViewReport}
        onClose={onClose}
        isSubmitting={isSubmitting}
        isSubmitDisabled={isSubmitDisabled}
        submissionError={submissionError}
        submissionErrorMessage={submissionErrorMessage}
      />
    );
  }

  const titleText = isResubmission ? copy.submissionDialog.resubmitTitle : copy.submissionDialog.initialTitle;
  const confirmText = isResubmission ? "Confirm Resubmission" : "Confirm Submission";
  const descriptionText = isResubmission
    ? copy.submissionDialog.resubmitDescription
    : copy.submissionDialog.initialDescription;
  const footerText = isResubmission ? copy.submissionDialog.resubmitFooter : copy.submissionDialog.initialFooter;
  const errorTitleText = isResubmission ? copy.submissionDialog.resubmitError : copy.submissionDialog.initialError;
  return (
    <Dialog
      open={open}
      setOpen={(next) => {
        if (!next) onClose();
      }}
      title=""
      size="xl"
      showCloseButton
      primaryButton={undefined}
      secondaryButton={undefined}
      bodyClassName="!px-8 pb-8"
      dialogClassName="!max-w-[37rem] sm:!max-w-[37rem]"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <Headline as="h2" size="large" colorType="primary">
            {titleText}
          </Headline>
          <Paragraph size="small" colorType="secondary">
            {descriptionText}
          </Paragraph>
        </div>

        <div className="flex flex-col gap-2 rounded-lg border border-limestone-200 bg-white p-4 ph-no-capture">
          <Paragraph size="medium" colorType="primary">
            {clientName}
          </Paragraph>
          <div className="flex flex-col">
            {dateOfBirth ? (
              <Paragraph size="small" colorType="secondary">
                DOB: {dateOfBirth}
              </Paragraph>
            ) : null}
            <Paragraph size="small" colorType="secondary">
              Report: {reportTypeLabel}
            </Paragraph>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            variant="primary"
            buttonStyle="border"
            size="lg"
            text="View report"
            onClick={onViewReport}
            disabled={isSubmitting}
            className="w-full justify-center"
          />
          <Button
            variant="primary"
            buttonStyle="filled"
            size="lg"
            text={
              <div className="flex items-center justify-center gap-2">
                {isSubmitting || isSubmitDisabled ? <Loader className="h-4 w-4" /> : null}
                {confirmText}
              </div>
            }
            onClick={onSubmitToPayor}
            disabled={isSubmitting || isSubmitDisabled}
            className="w-full justify-center"
          />
          <Paragraph size="extra-small" colorType="tertiary" className="mt-1 text-center">
            {footerText}
          </Paragraph>
        </div>

        {submissionError ? (
          <Alert
            appearance="error"
            title={errorTitleText}
            text={
              <p className="font-sans font-normal">
                {submissionErrorMessage ||
                  "Your report hasn't been sent — please try again. If the issue persists, contact support."}
              </p>
            }
            className="rounded-lg"
          />
        ) : null}
      </div>
    </Dialog>
  );
};
